<script setup lang="ts">
import UpsellModalForm from './UpsellModalForm.vue'
import BackgroundGradient from './BackgroundGradient.vue'
import type { SmsPricing, Upsell } from '../types/types'
const { t } = useI18n()
const { send } = useEvent()

const props = withDefaults(
  defineProps<{
    show: boolean
    helperCard: { title: string; steps: string[] }
    success: { title: string; text: string; linkText: string }
    links: { reminder: string; activate: string }
    isSms?: boolean
    title: string
    pricing?: number
    continueTrackingEvent?: string | null
  }>(),
  {
    show: false,
    isSms: false,
    pricing: 0,
    continueTrackingEvent: null
  }
)

const step = ref(props.isSms ? 0 : 1)

const smsPricing: SmsPricing[] = [
  {
    smsCount: 300,
    pricePerSms: 0.08333,
    pricePerMonth: 25,
    popular: false
  },
  {
    smsCount: 800,
    pricePerSms: 0.078,
    pricePerMonth: 62,
    popular: false
  },
  {
    smsCount: 2000,
    pricePerSms: 0.072,
    pricePerMonth: 144,
    popular: false
  },
  {
    smsCount: 4500,
    pricePerSms: 0.062,
    pricePerMonth: 279,
    popular: true
  },
  {
    smsCount: 10000,
    pricePerSms: 0.058,
    pricePerMonth: 580,
    popular: false
  }
]

const data = ref<Upsell[]>([])

const handleModalUpdate = (upsellInfos: Upsell[]) => {
  data.value = upsellInfos
}

const totalHtPrice = computed(() => {
  return data.value.map((d) => d.price).reduce((accumulator, currentValue) => accumulator + currentValue, 0)
})

const headingTitle = computed(() => {
  switch (step.value) {
    case 0:
      return t('upsell.smsPack.title')
    case 1:
      return props.title
    default:
      return ''
  }
})

const handleStep = (nextStep: number) => {
  if (props.continueTrackingEvent) {
    send('segment', props.continueTrackingEvent)
  }
  step.value = nextStep
}

const emit = defineEmits<{ (e: 'onCloseModal'): void; (e: 'onActivate', selectedStores: Upsell[]): void }>()

const close = () => {
  step.value = props.isSms ? 0 : 1
  emit('onCloseModal')
  handleStep(step.value)
}

const handleActivation = () => {
  emit('onActivate', data.value)
  handleStep(2)
}
</script>
<template>
  <BaseModal
    :is-open="show"
    :heading-title="headingTitle"
    size="xl"
    :text-title="step === 2 && success.title ? success.title : ''"
    :text="step === 2 && success.text ? success.text : ''"
    :link-text="step == 0 ? t('upsell.smsPack.continue') : step === 2 && success.linkText ? success.linkText : ''"
    :icon="step === 2 && 'HeroCheckIconSolid'"
    icon-color="green"
    class="sm:block"
    @close="close"
    @click="handleStep(1)"
  >
    <div v-if="step === 0" class="text-left">
      <p class="mb-2 text-sm text-foreground-secondary">{{ t('upsell.smsPack.subtitle') }}</p>
      <div class="mt-4 flex w-full flex-col gap-2 sm:mt-2">
        <div
          v-for="(sms, index) in smsPricing"
          :key="index"
          class="mx-4 flex flex-col items-center justify-between gap-2 rounded-md border p-3 md:flex-row md:gap-0"
        >
          <div class="w-max rounded-md bg-yellow-300 p-1.5 text-lg font-semibold text-primary">
            <p>{{ t('upsell.smsPack.label', { sms: sms.smsCount }) }}</p>
          </div>
          <div
            v-if="sms.popular"
            class="flex items-center gap-1 rounded-md bg-primary/5 p-2 text-xs font-medium text-primary"
          >
            <BaseIcon name="HeroSparklesIconSolid" class="h-3 w-3 text-yellow-400" />
            <p>{{ t('upsell.smsPack.popular') }}</p>
          </div>
          <div>
            <span class="pr-1 text-xs text-muted-foreground">{{
              t('upsell.smsPack.pricePerSms', { price: sms.pricePerSms })
            }}</span>
            <span class="text-sm font-semibold text-primary">{{
              t('upsell.smsPack.pricePerMonth', { price: sms.pricePerMonth })
            }}</span>
          </div>
        </div>
      </div>
    </div>
    <div v-if="step === 1" class="mt-16 text-left sm:mt-2">
      <div class="mb-5 flex w-full flex-col rounded-md bg-muted">
        <UpsellModalForm v-if="isSms" :sms-pricing="smsPricing" @on-update-modal="handleModalUpdate" />
        <UpsellModalForm v-else :pricing="pricing" @on-update-modal="handleModalUpdate" />
        <div class="flex items-center justify-between border-t px-5 pt-3">
          <p class="text-sm font-semibold">{{ t('upsell.modal.total.label') }}</p>
          <div class="text-right">
            <p v-if="data.length > 0" class="text-sm font-semibold">
              {{ t('upsell.modal.total.price', { price: totalHtPrice }) }}
            </p>
            <p class="text-xs text-muted-foreground">{{ t('upsell.modal.total.perMonth') }}</p>
          </div>
        </div>
        <p class="mt-2 px-5 pb-3 text-left text-xs text-muted-foreground">
          {{ t('upsell.modal.mandate') }}
        </p>
      </div>
      <BackgroundGradient>
        <div class="py-4">
          <p class="mb-4 w-full font-extrabold">{{ helperCard.title }}</p>
          <div class="flex w-full flex-col gap-2">
            <p v-for="helperStep in helperCard.steps" :key="helperStep" class="text-sm text-foreground">
              👉
              <span class="ml-2">{{ helperStep }}</span>
            </p>
          </div>
        </div>
      </BackgroundGradient>
      <div class="w-fill mx-2 my-2 mt-6 flex gap-2">
        <Button variant="outline" class="w-full" @click="emit('onCloseModal')">
          <a :href="links.reminder" target="_blank" class="p-1 text-sm font-medium">
            {{ t('upsell.modal.buttons.reminder') }}
          </a>
        </Button>
        <Button variant="default-primary" class="w-full" @click="handleActivation">
          <a :href="links.activate" class="p-1 text-sm font-medium">{{ t('upsell.modal.buttons.activate') }}</a>
        </Button>
      </div>
    </div>
  </BaseModal>
</template>
